import { useState, useRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useMutation } from '@apollo/client'
import { getCarfile } from 'redux/actions/data'
import { INSERT_PARTY } from 'config/graphql/v4'

/**
 * Custom hook to handle GPSR dialog logic
 *
 * @param {Object} carFile - The car file object containing GPSR data
 * @param {string} carId - The car ID for refreshing data after submission
 * @param {boolean} showGpsrForm - Optional condition to control when dialog should show (default: true)
 * @returns {Object} - Dialog state and handlers
 */
const useGpsrDialog = (carFile, carId, showGpsrForm = true) => {
  const dispatch = useDispatch()
  const [isGpsrDialogOpen, setIsGpsrDialogOpen] = useState(false)
  const formikRef = useRef(null)

  /**
   * Insert a new GPSR party data
   */
  const [insertParty] = useMutation(INSERT_PARTY)

  // Handle dialog open/close based on carFile.gpsrMessageOff and carFile.gpsrUserRequest
  useEffect(() => {
    if (
      showGpsrForm &&
      carFile &&
      !carFile.gpsrMessageOff &&
      carFile.gpsrUserRequest
    ) {
      setIsGpsrDialogOpen(true)
    } else if (carFile && !carFile.gpsrUserRequest && carFile.gpsrMessageOff) {
      setIsGpsrDialogOpen(false)
    }
  }, [showGpsrForm, carFile])

  const handleCloseGpsrDialog = () => {
    setIsGpsrDialogOpen(false)
  }

  const handleGpsrFormSubmit = async (values) => {
    try {
      const gpsrPartyData = {
        type: values.type,
        name: values.manufacturerName,
        address: values.address,
        postal_code: values.postalCode,
        city: values.place,
        province: values.province,
        phone_number: values.phoneNumber,
        website: values.website,
        email_address: values.email,
      }

      const payload = Object.fromEntries(
        Object.entries(gpsrPartyData)
          .map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
          .filter(([, v]) => v != null && v !== ''),
      )

      const hasGpsrPartyData = Object.keys(payload).length > 0

      await insertParty({
        variables: {
          brand: carFile.merk,
          svid_merk: Number(carFile.svidMerk),
          category_id: Number(carFile.gpsrCategoryId),
          carfileId: Number(carId),
          gpsr_message_off: Boolean(values.dontShowGPSRMessageAgain),
          data: hasGpsrPartyData ? payload : {},
        },
      })

      if (carId) {
        dispatch(getCarfile(carId))
      }

      setIsGpsrDialogOpen(false)
    } catch (err) {
      console.error('Error submitting GPSR form:', err)
    }
  }

  return {
    isGpsrDialogOpen,
    formikRef,
    handleCloseGpsrDialog,
    handleGpsrFormSubmit,
  }
}

export default useGpsrDialog
